import React from 'react';
import { Shield, Globe, Clock, Database, Share2, Lock, UserCheck } from 'lucide-react';

const PrivacyPolicyPage: React.FC = () => {
  const lastUpdate = '13 de agosto de 2026';
  const responsable = {
    nombre: 'SoArtistic',
    email: 'privacy@soartistic.es',
    domicilio: 'España',
  };

  const sections = [
    {
      title: '1. Identidad del Responsable del Tratamiento',
      Icon: Shield,
      body: (
        <ul className="list-disc pl-6 space-y-2">
          <li>{responsable.nombre} en calidad de Responsable del Tratamiento de Datos.</li>
          <li>Contacto: <a className="text-purple-600 underline" href={`mailto:${responsable.email}`}>{responsable.email}</a>.</li>
          <li>Domicilio: {responsable.domicilio}.</li>
          <li>Delegado de Protección de Datos (DPD): el mismo correo electrónico.</li>
        </ul>
      ),
    },
    {
      title: '2. Finalidades del Tratamiento',
      Icon: TargetIcon,
      body: (
        <ul className="list-disc pl-6 space-y-2">
          <li>Gestión de la cuenta de usuario, autenticación y soporte técnico.</li>
          <li>Publicación y visualización de obras, comentarios, likes y colecciones.</li>
          <li>Procesamiento de suscripciones y compras a través de Stripe (PCI-DSS).</li>
          <li>Emisión de facturas y notificaciones fiscales vía Holded.</li>
          <li>Verificación de edad mediante Didit (KYC) para contenido sensible.</li>
          <li>Análisis agregado y anonimizado de uso mediante Plausible.</li>
          <li>Cumplimiento legal, moderación de contenido y prevención de fraude.</li>
        </ul>
      ),
    },
    {
      title: '3. Base Legal',
      Icon: ScaleIcon,
      body: (
        <ul className="list-disc pl-6 space-y-2">
          <li>Consentimiento explícito (RGPD art. 6.1.a) al crear la cuenta.</li>
          <li>Ejecución del contrato de prestación de servicios (art. 6.1.b).</li>
          <li>Cumplimiento de obligaciones legales (art. 6.1.c) — fiscal, facturación, LOPDGDD.</li>
          <li>Interés legítimo (art. 6.1.f) para mejora del servicio y moderación.</li>
        </ul>
      ),
    },
    {
      title: '4. Categorías de Datos',
      Icon: Database,
      body: (
        <ul className="list-disc pl-6 space-y-2">
          <li>Identificativos: nombre, email, id Supabase, avatar.</li>
          <li>Perfil: biografía, enlaces sociales, username, tipo de cuenta.</li>
          <li>Financieros: solo last-4, brand y expiry de tarjeta almacenados por Stripe; SoArtistic no conserva PANs.</li>
          <li>Uso: publicaciones, visualizaciones, preferencias, eventos y reportes.</li>
          <li>Edad: estado verificado / rechazado por Didit (sin documentos de identidad permanentes).</li>
        </ul>
      ),
    },
    {
      title: '5. Destinatarios y Transferencias',
      Icon: Share2,
      body: (
        <ul className="list-disc pl-6 space-y-2">
          <li>Encargados: Supabase (UE + EE.UU. con SCCs), Stripe (UE), Holded (UE), Didit (UE), Plausible (UE).</li>
          <li>No existen otras transferencias internacionales sin medidas adecuadas.</li>
          <li>Las autoridades competentes solo accederán a los datos si existe una obligación legal.</li>
        </ul>
      ),
    },
    {
      title: '6. Conservación',
      Icon: Clock,
      body: (
        <ul className="list-disc pl-6 space-y-2">
          <li>Cuenta activa: datos conservados mientras dure la relación contractual.</li>
          <li>Tras baja: perfil eliminado con <code>delete_user_cascade</code> en máximo 30 días; facturas conservadas 6 años por normativa fiscal.</li>
          <li>Stories automáticamente purgadas mediante <code>cleanup_expired_stories</code>.</li>
        </ul>
      ),
    },
    {
      title: '7. Derechos ARCO-SUL',
      Icon: UserCheck,
      body: (
        <ul className="list-disc pl-6 space-y-2">
          <li>Acceso, rectificación, supresión (“derecho al olvido”), limitación, portabilidad y oposición.</li>
          <li>Retirada de consentimiento en cualquier momento desde el panel de Ajustes.</li>
          <li>Ejercicio vía <a className="text-purple-600 underline" href={`mailto:${responsable.email}`}>{responsable.email}</a>.</li>
          <li>Reclamación ante AEPD si considera vulnerado su derecho.</li>
        </ul>
      ),
    },
    {
      title: '8. Medidas de Seguridad',
      Icon: Lock,
      body: (
        <ul className="list-disc pl-6 space-y-2">
          <li>Cifrado TLS en tránsito; cifrado en reposo en Postgres + Supabase Storage.</li>
          <li>RLS (Row Level Security) habilitado en todas las tablas públicas.</li>
          <li>Anonimización de datos de analítica y rotación de credenciales.</li>
          <li>Copias de seguridad automáticas y plan de respuesta a incidentes.</li>
        </ul>
      ),
    },
    {
      title: '9. Cookies',
      Icon: CookieIcon,
      body: (
        <ul className="list-disc pl-6 space-y-2">
          <li>Esenciales de autenticación y sesión (obligatorias).</li>
          <li>Analíticas anónimas (Plausible) sin tracking cruzado; se pueden desactivar desde Ajustes.</li>
          <li>Terceros: Stripe (pago), Didit (verificación) — cada uno con su política específica.</li>
        </ul>
      ),
    },
    {
      title: '10. Cambios en la Política',
      Icon: Globe,
      body: (
        <ul className="list-disc pl-6 space-y-2">
          <li>Cambios notificados por email y aviso destacado en la aplicación.</li>
          <li>Versión anterior conservada durante 6 meses para auditoría.</li>
        </ul>
      ),
    },
  ];

  return (
    <article className="mx-auto max-w-3xl px-6 py-16 text-slate-800">
      <header className="mb-10 border-b pb-8">
        <p className="text-sm uppercase tracking-widest text-purple-600 mb-2">Anexo D.3</p>
        <h1 className="text-4xl font-black tracking-tight mb-3">Política de Privacidad de SoArtistic</h1>
        <p className="text-slate-500">Última actualización: {lastUpdate}.</p>
        <p className="mt-3">
          Cumplimiento del Reglamento (UE) 2016/679 (RGPD) y Ley Orgánica 3/2018, de 5 de diciembre, de
          Protección de Datos y Garantía de los Derechos Digitales (LOPDGDD).
        </p>
      </header>

      <div className="space-y-10">
        {sections.map(({ title, Icon, body }) => (
          <section key={title} className="scroll-mt-24" id={slugify(title)}>
            <div className="flex items-start gap-3 mb-3">
              <div className="mt-1 rounded-lg bg-purple-50 p-2 text-purple-700">
                <Icon size={18} />
              </div>
              <h2 className="text-xl font-bold">{title}</h2>
            </div>
            <div className="text-sm leading-relaxed text-slate-700 pl-12">{body}</div>
          </section>
        ))}
      </div>

      <footer className="mt-16 rounded-xl border bg-slate-50 p-6 text-xs text-slate-500">
        Documento generado para cumplimentar el cuestionario de App Privacy de App Store Connect y la
        Política de privacidad obligatoria para Google Play. URL pública:{' '}
        <a className="underline" href="/privacy">/privacy</a>.
      </footer>
    </article>
  );
};

function TargetIcon(props: { size?: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={props.size ?? 24} height={props.size ?? 24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" />
    </svg>
  );
}

function ScaleIcon(props: { size?: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={props.size ?? 24} height={props.size ?? 24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 16l3.8-3.8a2 2 0 0 0 0-2.8L16 6" /><path d="M8 16l-3.8-3.8a2 2 0 0 1 0-2.8L8 6" /><line x1="12" y1="3" x2="12" y2="21" />
    </svg>
  );
}

function CookieIcon(props: { size?: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={props.size ?? 24} height={props.size ?? 24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21.6 11.5a9 9 0 1 1-10-10" /><path d="M14 12h.01" /><path d="M10 17h.01" /><path d="M7 10h.01" /><path d="M17 7h.01" />
    </svg>
  );
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export default PrivacyPolicyPage;
